import { useEffect } from 'react'
import { createRoot } from 'react-dom/client'
import { ClerkProvider, useAuth } from '@clerk/clerk-react'
import { esES } from '@clerk/localizations'
import { ConvexProviderWithClerk } from 'convex/react-clerk'
import { ConvexReactClient } from 'convex/react'
import { BrowserRouter } from 'react-router-dom'
import App from './App'
import { setAuthTokenGetter } from './lib/api'
import './index.css'

// Clerk publishable key from environment
const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY

if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY environment variable')
}

// Convex URL from environment
const CONVEX_URL = import.meta.env.VITE_CONVEX_URL

if (!CONVEX_URL) {
  throw new Error('Missing VITE_CONVEX_URL environment variable')
}

// Create Convex client
const convex = new ConvexReactClient(CONVEX_URL)

/**
 * Component that initializes the auth token getter for API calls.
 * Must be rendered inside ClerkProvider to access useAuth hook.
 */
function AuthInitializer({ children }: { children: React.ReactNode }) {
  const { getToken } = useAuth()

  useEffect(() => {
    // Set up the token getter for the API module
    setAuthTokenGetter(async () => {
      try {
        return await getToken()
      } catch {
        return null
      }
    })
  }, [getToken])

  return <>{children}</>
}

createRoot(document.getElementById('root')!).render(
  <ClerkProvider publishableKey={CLERK_PUBLISHABLE_KEY} localization={esES}>
    <ConvexProviderWithClerk client={convex} useAuth={useAuth}>
      <AuthInitializer>
        <BrowserRouter>
          <App />
        </BrowserRouter>
      </AuthInitializer>
    </ConvexProviderWithClerk>
  </ClerkProvider>
)
